<template>
  <page-view :menu="menu">
    <template #header>
      <base-header :title="$t('message.settings')" />
    </template>

    <template #content>
      <v-row no-gutters v-if="isRootUser">
        <v-col cols="12">
          <v-row align="center" no-gutters>
            <div class="icon-container mr-3">
              <v-icon color="turquisePanda" size="28">mdi-key-variant</v-icon>
            </div>
            <span class="text-h5">{{ $t('message.pages.apikey.apikey') }}</span>
            <span class="caption ml-3 text--secondary">
              | {{ $t('message.pages.apikey.apikeySubtitle') }}
            </span>
          </v-row>
        </v-col>
        <template v-if="getLoading">
          <v-card flat width="700" height="250">
            <loading-logo :value="200" />
          </v-card>
        </template>
        <v-col class="mt-6 pa-0 pr-5" cols="12" v-else>
          <template slot="progress">
            <v-progress-linear
              color="primary"
              height="5"
              indeterminate
            ></v-progress-linear>
          </template>
          <v-row align="center">
            <v-col cols="4" style="width: 450px">
              <span class="text-body-1 font-weight-bold">{{
                $t('message.generateNewKey')
              }}</span>
            </v-col>
            <v-col>
              <v-divider></v-divider>
            </v-col>
          </v-row>
          <v-col cols="12" class="mt-2 pa-0">
            <span class="caption">
              {{ $t('message.apiKeyText') }}
              <a href="https://pandavideo.readme.io" target="_blank">
                {{ $t('message.consult') }}</a
              >
              {{ $t('message.apiKeyText2') }}
            </span>
          </v-col>
          <v-col v-if="getLoading" cols="6" class="pa-0 mt-8 mb-10">
            <v-skeleton-loader loading type="text"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" class="pa-0 mt-6" v-else>
            <v-text-field
              v-if="apiKey"
              v-model="apiKey"
              append-icon="mdi-content-copy"
              type="text"
              @click:append="copyTextToClipboard"
              @click="copyTextToClipboard"
              hide-details
              outlined
              dense
              :label="
                getLoading ? $t('message.gettingKey') : $t('message.apiKey')
              "
              color="primary"
              readonly
            ></v-text-field>
            <v-text-field
              v-if="hasApiKey && !apiKey"
              :value="Array(60).fill('*').join('')"
              type="password"
              outlined
              filled
              hide-details
              dense
              :label="$t('message.apiKey')"
              color="primary"
              readonly
              disabled
            ></v-text-field>
          </v-col>
          <v-card-actions class="pa-0 mt-4">
            <v-btn
              v-if="hasApiKey"
              plain
              class="ma-0 pl-0"
              color="error"
              :disabled="!!!hasApiKey || loading"
              @click="handleDeleteKey"
            >
              {{ $t('message.deleteKey') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              min-width="250px"
              :disabled="loading"
              color="primary"
              @click="handleGenerateKey"
            >
              <span class="mx-2">{{ $t('message.generateNewKey') }}</span>
            </v-btn>
          </v-card-actions>
        </v-col>
      </v-row>
      <v-row v-if="isRootUser">
        <v-col cols="12">
          <v-divider class="my-2" />
        </v-col>
      </v-row>
      <v-row class="pl-5">
        <v-col cols="12">
          <v-row align="center">
            <div class="icon-container mr-3">
              <v-icon color="turquisePanda">
                mdi-upload-network-outline
              </v-icon>
            </div>
            <span class="text-h5">{{
              $t('message.pages.advanced.server')
            }}</span>
            <span class="caption ml-3 text--secondary"
              >| {{ $t('message.pages.advanced.serverSubtitle') }}</span
            >
          </v-row>
        </v-col>
        <template v-if="getLoading">
          <v-card flat width="700" height="250">
            <loading-logo :value="200" />
          </v-card>
        </template>
        <v-col class="mt-6 pa-0 pr-5" cols="12" v-else>
          <template slot="progress">
            <v-progress-linear
              color="primary"
              height="5"
              indeterminate
            ></v-progress-linear>
          </template>
          <v-row align="center">
            <v-col cols="4" style="width: 450px">
              <span class="text-body-1 font-weight-bold">{{
                $t('message.pages.advanced.serverMainText')
              }}</span>
            </v-col>
            <v-col>
              <v-divider></v-divider>
            </v-col>
          </v-row>
          <v-col cols="12" class="mt-2 pa-0">
            <span class="caption">
              {{ $t('message.pages.advanced.serverText1') }}
            </span>
          </v-col>
          <v-col v-if="getLoading" cols="6" class="pa-0 mt-8 mb-10">
            <v-skeleton-loader loading type="text"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" class="pa-0 mt-6" v-else>
            <v-menu
              ref="menu"
              v-model="menuUpload"
              :close-on-content-click="false"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-select
                  v-model="selectedServer"
                  :disabled="getHostsLoading"
                  :items="hosts"
                  item-text="name"
                  item-value="server"
                  class="mt-2"
                  dense
                  hide-details
                  :label="$t('message.pages.advanced.server')"
                  type="text"
                  :menu-props="{ top: false, offsetY: true }"
                  :placeholder="$t('message.pages.advanced.server')"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                >
                </v-select>
              </template>
            </v-menu>
          </v-col>
          <v-card-actions class="pa-0 mt-4">
            <v-spacer></v-spacer>
            <v-btn
              min-width="250px"
              color="primary"
              :disabled="serverLoading"
              :loading="serverLoading"
              @click="handleChangeHost"
            >
              <span class="mx-2">{{
                $t('message.pages.advanced.confirmChange')
              }}</span>
            </v-btn>
          </v-card-actions>
        </v-col>
        <v-col class="mt-5 pa-0 pr-5" cols="12">
          <template slot="progress">
            <v-progress-linear
              color="primary"
              height="5"
              indeterminate
            ></v-progress-linear>
          </template>
          <v-row align="center">
            <v-col cols="4" style="width: 450px">
              <span class="text-body-1 font-weight-bold">{{
                $t('message.pages.advanced.downloadServers')
              }}</span>
            </v-col>
            <v-col>
              <v-divider></v-divider>
            </v-col>
          </v-row>
          <v-col cols="12" class="mt-2 pa-0">
            <span class="caption">
              {{ $t('message.pages.advanced.serverText2') }}
            </span>
          </v-col>
          <template v-if="getLoading">
            <v-card flat width="700" height="250">
              <loading-logo :value="200" />
            </v-card>
          </template>
          <v-col cols="12" class="pa-0 mt-6" v-else>
            <v-menu
              ref="menu"
              v-model="menuDownload"
              :close-on-content-click="false"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-select
                  v-model="selectedDownloadServer"
                  :disabled="getHostsLoading"
                  :items="hosts"
                  item-text="name"
                  item-value="server"
                  class="mt-2"
                  dense
                  hide-details
                  :label="$t('message.pages.advanced.server')"
                  type="text"
                  :menu-props="{ top: false, offsetY: true }"
                  :placeholder="$t('message.pages.advanced.server')"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                >
                </v-select>
              </template>
            </v-menu>
          </v-col>
          <v-card-actions class="pa-0 mt-4" v-if="!getLoading">
            <v-spacer></v-spacer>
            <v-btn
              min-width="250px"
              color="primary"
              :disabled="serverDownloadLoading"
              :loading="serverDownloadLoading"
              @click="handleChangeDownloadHost"
            >
              <span class="mx-2">{{
                $t('message.pages.advanced.confirmChange')
              }}</span>
            </v-btn>
          </v-card-actions>
        </v-col>
      </v-row>
    </template>
  </page-view>
</template>

<script>
// external imports
import { mapMutations } from 'vuex'

// internal imports
import i18n from '@/translations/lang'
import Storage from '@/utils/Storage'

// API
import userApi from '@/services/userApi'
import hostsApi from '@/services/hostsApi'

// Components
import PageView from '@/layout/PageView.vue'
import LoadingLogo from '@/components/LoadingLogo.vue'

export default {
  name: 'Advanced',
  components: {
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
    LoadingLogo,
  },
  data: () => ({
    apiKey: '',
    hasApiKey: false,
    open: false,
    loading: false,
    getLoading: false,
    getHostsLoading: false,
    selectedServer: '',
    serverLoading: false,
    menuUpload: false,
    menuDownload: false,
    hosts: [
      {
        name: i18n.t('message.pages.advanced.defaultOption'),
        server: 'default',
        country: 'br',
      },
    ],
    selectedDownloadServer: '',
    serverDownloadLoading: false,
  }),
  computed: {
    isRootUser() {
      return this.$store.state.user?.userAllInfo?.is_root ?? false
    },
    menu() {
      return this.$store.state.user.settingsNavItems
    },
  },
  async mounted() {
    this.getLoading = true
    Promise.all[(await this.fetchHosts(), await this.handleGetApiKey())]
    this.getHostsLoading = false
    this.getLoading = false
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    copyTextToClipboard() {
      if (!this.apiKey) {
        return
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.copiedToClipboard'),
      })
      navigator.clipboard.writeText(`${this.apiKey}`)
    },
    async handleGetApiKey() {
      this.loading = true
      try {
        let ret = await userApi.getApiKey()
        this.hasApiKey = !!ret.data.apikey
        this.apiKey = ret.data.apikey.api_key
      } catch (e) {
        const error = e.response.data
        if (error.errMsg === 'NotFound') {
          this.loading = false
          return
        }
        console.log(error)
      }
      this.loading = false
    },
    setDefaultHost() {
      if (Storage.getItem('defaultUploader')) {
        this.selectedServer = 'default'
        return
      }
      const savedServer = Storage.getItem('uploaderUrlv3')
      let localServer = savedServer.substring(
        savedServer.indexOf('/') + 2,
        savedServer.indexOf('.')
      )
      if (savedServer) {
        this.selectedServer = localServer
      } else {
        this.selectedServer = 'default'
      }
    },
    setDefaultHostDownload() {
      if (Storage.getItem('defaultDownloader')) {
        this.selectedDownloadServer = 'default'
        return
      }
      const savedServer = Storage.getItem('downloaderServer')
      if (savedServer) {
        let savedHost = this.hosts.find((host) =>
          host.server.includes(savedServer)
        )
        this.selectedDownloadServer = savedHost.server
      } else {
        this.selectedDownloadServer = 'default'
      }
    },
    handleChangeHost() {
      if (this.selectedServer === 'default') {
        Storage.setItem('defaultUploader', true)
      } else {
        Storage.removeItem('defaultUploader')
      }
      this.serverLoading = true
      setTimeout(() => {
        this.$root.$emit('uppyChangeHost', this.selectedServer)
        this.serverLoading = false
      }, 1000)

      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.pages.advanced.changeServer', { type: 'Upload' }),
      })
    },
    handleChangeDownloadHost() {
      if (this.selectedDownloadServer === 'default') {
        Storage.setItem('defaultDownloader', true)
      } else {
        Storage.removeItem('defaultDownloader')
      }
      this.serverDownloadLoading = true
      setTimeout(() => {
        if (this.selectedDownloadServer === 'default') {
          Storage.setItem(
            'downloaderServer',
            this.returnOnlyServerTag(this.hosts[1].server)
          )
        } else {
          Storage.setItem(
            'downloaderServer',
            this.returnOnlyServerTag(this.selectedDownloadServer)
          )
        }
        this.serverDownloadLoading = false
      }, 1000)

      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.pages.advanced.changeServer', {
          type: 'Download',
        }),
      })
    },
    returnOnlyServerTag(fullServer) {
      let splitServer = fullServer.split('-')
      return splitServer[splitServer.length - 1]
    },
    async fetchHosts() {
      this.getHostsLoading = true
      try {
        const ret = await hostsApi.getHosts()
        for (let hostArea in ret.data.hosts) {
          for (let i = 0; i < ret.data.hosts[hostArea].length; i++) {
            let host = ret.data.hosts[hostArea][i]
            if (host) {
              let country = host.substring(host.length - 4, host.length)
              let countryShort = country.substring(0, 2)
              let serverNumber = country.substring(
                country.length - 2,
                country.length
              )
              const serverName = `${countryShort.toUpperCase()}-${serverNumber}`
              this.hosts.push({
                name: serverName,
                server: host,
                country: countryShort,
              })
            }
          }
        }
        this.setDefaultHost()
        this.setDefaultHostDownload()
      } catch (e) {
        console.error(e)
      }
      this.getHostsLoading = false
    },
    async handleGenerateKey() {
      this.loading = true
      try {
        let ret = await userApi.createApiKey()
        this.apiKey = ret.data.apikey.api_key
        this.hasApiKey = !!ret.data.apikey
      } catch (e) {
        console.log(e)
        this.loading = false
      }
      this.loading = false
    },
    async handleDeleteKey() {
      this.loading = true
      try {
        await userApi.deleteApiKey()
        this.apiKey = ''
        this.hasApiKey = false
      } catch (e) {
        console.log(e)
        this.loading = false
      }
      this.loading = false
    },
  },
}
</script>

<style>
.icon-container {
  background-color: #eaedf6;
  padding: 4px;
  border-radius: 3px;
}
.user-avatar {
  margin-top: -50px;
}
.attributesText {
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.05em;
  color: #333333;
}
.requirementsText {
  font-weight: normal;
  font-size: 12px;
  line-height: 18px;
  color: #878787;
}

.codeText {
  font-weight: 600;
  font-size: 28px;
  line-height: 42px;
}

.textFieldText {
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
}
</style>
